import { useMemo } from 'react';
import { ContractLang, ContractStyleType, ContractType } from '../utils';
import { getShopConfig } from 'constant/shop';
import { LANGUAGE_MAP } from 'constant/language';
import classNames from 'classnames';
import DisputeInfo from '../components/dispute-info';

const NormalContractFooter = ({
  contractType,
  contractLang,
  storeId,
  currencySymbol,
  totalPrice,
  contractStyleType,
  isStoreCopy,
  signatureImage,
}: {
  contractType: ContractType;
  contractLang: ContractLang;
  storeId: number;
  currencySymbol: string;
  totalPrice: number;
  contractStyleType: ContractStyleType;
  isStoreCopy: boolean;
  signatureImage?: string;
}) => {
  const isRecycleContract =
    contractType === 'RECYCLE_CUSTOMER' || contractType === 'RECYCLE_STORE';
  const isSellContract =
    contractType === 'SELL_CUSTOMER' || contractType === 'SELL_STORE';
  const storeName = getShopConfig(storeId).companyName;
  const showSignatureArea = isStoreCopy || !!signatureImage;

  // 回收相关条款
  const recycleRuleEl = useMemo(() => {
    if (contractLang === LANGUAGE_MAP.JA) {
      return (
        <>
          ご契約後のお買戻しはお受付できませんのでご了承ください。
          <br />
          ご契約後、商品が不正商品と発覚した場合、全額返金いただく場合があります。
        </>
      );
    }

    if (contractLang === LANGUAGE_MAP.FR) {
      return (
        <>
          Aucun remboursement après le rachat.
          <br />
          Si le produit s&apos;avère être une contrefaçon ou un article volé,
          <br />
          le client est tenu de rembourser. {storeName} se réserve le droit
          <br />
          d&apos;engager des poursuites judiciaires.
        </>
      );
    }

    if (contractLang === LANGUAGE_MAP.EN) {
      return (
        <>
          No refund after repurchase.
          <br />
          If the product was found to be counterfeit or stolen item,
          <br />
          the customer is responsible to refund Asubrand Company Limited
          <br />
          reserve the right to pursue legal action.
        </>
      );
    }

    if (contractLang === LANGUAGE_MAP.ZH_CN) {
      return (
        <>
          契约成立后不予退款。
          <br />
          如发现商品为假货或赃物，
          <br />
          客户有责任向 晓马（上海）贸易有限公司 退款，
          <br />
          本公司保留追究法律责任的权利。
        </>
      );
    }

    return null;
  }, [contractLang, storeName]);

  // 寄卖相关条款
  const sellRuleEl = useMemo(() => {
    if (contractLang === LANGUAGE_MAP.JA) {
      return (
        <>
          ○ 本商品は、お預かり日より１か月以上お預かりするものとし、
          <br />
          １か月未満での委託販売中止は基本出来ないものとします（要ご相談）
          <br />
          ○ 本商品の売却が成立した場合、決済確定後にお知らせいたします。
          <br />
          お知らせ後はご相談によりお振込みまたは現金にて表記の金額をお支払いいたします。
        </>
      );
    }

    if (contractLang === LANGUAGE_MAP.FR) {
      return (
        <>
          La période de consignation est de 1 mois.
          <br />
          Veuillez nous contacter si vous souhaitez annuler la consignation à
          l&apos;avance.
          <br />
          Une fois la vente conclue, nous vous contacterons immédiatement et
          transférerons
          <br />
          le montant convenu sur le compte désigné.
        </>
      );
    }

    if (contractLang === LANGUAGE_MAP.EN) {
      return (
        <>
          Consignment period is 1 months.
          <br />
          Please contact us if you want to cancel consignment in advance.
          <br />
          Once the consignment is sold, we will contact you immediately and
          transfer
          <br />
          the agreement amount to your designated account.
        </>
      );
    }

    if (contractLang === LANGUAGE_MAP.ZH_CN) {
      return (
        <>
          寄卖期为1个月。
          <br />
          如需提前取消寄卖，请与我们联系。
          <br />
          一旦商品售出，我们会立即与您联系，
          <br />
          并将协议金额转账至您指定的账户。
        </>
      );
    }

    return null;
  }, [contractLang]);

  const priceArr = useMemo(() => {
    const el1 = [
      {
        [LANGUAGE_MAP.EN]: 'Sub Total',
        [LANGUAGE_MAP.JA]: '小計',
        [LANGUAGE_MAP.ZH_CN]: '小记',
        [LANGUAGE_MAP.FR]: 'Sous-total',
      }[contractLang],
      currencySymbol + ' ' + totalPrice.toLocaleString(),
    ];
    const el2 = [
      '消費税10%対象 内税額',
      currencySymbol + ' ' + Math.floor(totalPrice / 11).toLocaleString(),
    ];
    const el3 = [
      {
        [LANGUAGE_MAP.EN]: 'Total',
        [LANGUAGE_MAP.JA]: '総合計',
        [LANGUAGE_MAP.ZH_CN]: '合计',
        [LANGUAGE_MAP.FR]: 'Total',
      }[contractLang],
      currencySymbol + ' ' + totalPrice.toLocaleString(),
    ];

    if (contractLang === LANGUAGE_MAP.JA) return [el1, el2, el3];

    return [el1, el3];
  }, [contractLang, currencySymbol, totalPrice]);

  return (
    <>
      <DisputeInfo contractLang={contractLang} storeName={storeName} />
      <div className="border-l border-r px-2 py-2 shrink-0">
        <div className="flex justify-between">
          <div className="text-[10px]">
            {isRecycleContract && recycleRuleEl}
            {isSellContract && sellRuleEl}
          </div>

          <div className="basis-1/3">
            {priceArr.map(([title, value], index) => {
              return (
                <div key={index} className="flex justify-between mb-1">
                  <span>{title}</span>
                  <span>{value}</span>
                </div>
              );
            })}
          </div>
        </div>

        {showSignatureArea && (
          <div className="mt-3">
            <div
              className={classNames(
                'relative mb-1 w-1/2 min-h-[48px] flex justify-between items-end pb-1',
              )}
              style={{ borderBottom: 'double' }}
            >
              <span>
                {
                  {
                    [LANGUAGE_MAP.EN]: 'Signature',
                    [LANGUAGE_MAP.JA]: '名前',
                    [LANGUAGE_MAP.ZH_CN]: '签名:',
                    [LANGUAGE_MAP.FR]: 'Signature',
                  }[contractLang]
                }
              </span>
              {signatureImage && (
                <img
                  src={signatureImage}
                  alt=""
                  className="absolute left-16 bottom-1 h-12 max-w-[180px] object-contain"
                />
              )}
              {contractLang === LANGUAGE_MAP.JA && <span>印</span>}
            </div>
            {contractLang === LANGUAGE_MAP.JA && (
              <div className="text-[11px]">
                <span>適格請求書（インボイス）発行事業者ではありません。</span>
                <span className="ml-2">口はい</span>
                <span className="ml-2">口いいえ</span>
                <span className="ml-4">適格請求畫発行事業者</span>
                <span className="ml-2">登録番号：</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className={classNames(
          'text-lg px-1 shrink-0',
          contractStyleType === 'white'
            ? 'bg-white text-black border'
            : 'bg-black text-white',
        )}
        style={{ printColorAdjust: 'exact' }}
      >
        {storeName.toUpperCase()}
      </div>
    </>
  );
};

export default NormalContractFooter;
